"use client";

import { NoStemSplitGroups } from "@/app/(application)/(normal)/stem-splitter/components/output/NoStemSplitGroups";
import { StemSplitGroupHistory } from "@/app/(application)/(normal)/stem-splitter/components/output/StemSplitGroupHistory";
import { AudioPlayerDownloadStemSplitGroup } from "@/components/audioPlayers/audioPlayerDownloadStemSplitGroup/AudioPlayerDownloadStemSplitGroup";
import { AudioOutputSkeleton } from "@/components/skeletons/audio/AudioOutputSkeleton";
import { Heading3 } from "@/components/text/Heading3";
import { SoftText } from "@/components/text/SoftText";
import {
  MAX_NUMBER_OF_STEM_SPLIT_GROUP_HISTORY,
  SECONDS_CHECK_IF_STEM_SPLIT_GROUP_UPDATE,
} from "@/constants/constants";
import { checkStemSplitGroupsPercentagesDoneAction } from "@/data/stemSplit/actions/checkStemSplitGroupsPercentagesDoneAction";
import { StemSplitGroupWithAudioNoDates } from "@/types/defs/stemSplit";
import { UserNoDates } from "@/types/defs/user";
import { useStatusPolling } from "@/utils/hooks/useStatusPolling";

const isAnyStemSplitGroupRunning = (
  stemSplitGroups: StemSplitGroupWithAudioNoDates[]
) =>
  stemSplitGroups.some(
    (stemSplitGroup) =>
      stemSplitGroup.confirmedDone !== true && stemSplitGroup.failed === false
  );

const checkStemSplitGroupsStatus = (
  stemSplitGroups: StemSplitGroupWithAudioNoDates[]
) =>
  checkStemSplitGroupsPercentagesDoneAction({
    stemSplitGroupsInput: stemSplitGroups.map((stemSplitGroup) => ({
      stemSplitGroupId: stemSplitGroup.id,
      percentageDone: stemSplitGroup.percentageDone,
      confirmedDone: stemSplitGroup.confirmedDone,
      failed: stemSplitGroup.failed,
    })),
  });

export function Output({
  user,
  stemSplitGroups,
  shouldShowLoadState,
}: {
  user: UserNoDates | null;
  stemSplitGroups: StemSplitGroupWithAudioNoDates[] | null;
  shouldShowLoadState: boolean;
}) {
  const shouldShowEmptyStemSplitGroups =
    user === null || stemSplitGroups === null || stemSplitGroups.length === 0;

  // If there are stem split groups that are running, setup polling
  useStatusPolling({
    pollInput: shouldShowLoadState ? null : stemSplitGroups,
    isRunning: isAnyStemSplitGroupRunning,
    checkStatus: checkStemSplitGroupsStatus,
    baseDelayMs: SECONDS_CHECK_IF_STEM_SPLIT_GROUP_UPDATE * 1000,
    statusLabel: "stem split groups",
  });

  return (
    <div className="h-fit w-full flex-1 rounded-3xl bg-gray-50 px-4 py-4 shadow-sm ring-1 ring-gray-900/5 sm:px-8 sm:py-8 xl:w-1/2">
      <Heading3 elementType="h3">Output</Heading3>
      <SoftText elementType="p" className="mb-4 mt-1">
        Your last {MAX_NUMBER_OF_STEM_SPLIT_GROUP_HISTORY} stem splits are
        saved. Download any you want to keep. Use grouped download for all
        stems, or download individual stems.
      </SoftText>
      {shouldShowLoadState && <AudioOutputSkeleton size="small" />}
      {!shouldShowLoadState && shouldShowEmptyStemSplitGroups && (
        <NoStemSplitGroups />
      )}
      {!shouldShowLoadState && !shouldShowEmptyStemSplitGroups && (
        <>
          <AudioPlayerDownloadStemSplitGroup
            stemSplitGroup={stemSplitGroups[0]}
          />
          {stemSplitGroups.length > 1 && (
            <StemSplitGroupHistory stemSplitGroups={stemSplitGroups.slice(1)} />
          )}
        </>
      )}
    </div>
  );
}
